import { useState, useEffect } from 'react'
import { Card, Badge, Loading } from '../components/common'
import { spkService } from '../services/spkService'
import { fileService } from '../services/fileService'
import { Zap, Download, Printer, FileText, CheckCircle, AlertTriangle, Clock } from 'lucide-react'

function ComcolorPage() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => { fetchQueue() }, [])

  const fetchQueue = async () => {
    try {
      setLoading(true)
      const res = await spkService.getComcolorQueue()
      if (res.success) setItems(res.data.items || [])
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal memuat antrian')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (file) => {
    try {
      const response = await fileService.download(file.id)
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = file.filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      alert('Gagal download file: ' + (e.response?.data?.message || e.message))
    }
  }

  if (loading) return <Loading text="Memuat antrian..." />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-emerald-600" /> Cetak Comcolor
          </h1>
          <p className="text-gray-500">Download file, cetak, dan input hasil — langsung siap binding (skip susun)</p>
        </div>
        <button
          onClick={fetchQueue}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
        >
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {items.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500">
            <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Tidak ada antrian cetak Comcolor</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(item => (
            <ComcolorCard
              key={item.print_id}
              item={item}
              onDownload={handleDownload}
              onOpen={() => setSelected(item)}
            />
          ))}
        </div>
      )}

      {selected && (
        <CompleteModal
          item={selected}
          onClose={() => setSelected(null)}
          onSuccess={() => { setSelected(null); fetchQueue() }}
        />
      )}
    </div>
  )
}

function ComcolorCard({ item, onDownload, onOpen }) {
  const stationLabel = { cetak_isi: 'Cetak Isi', potong_isi: 'Potong Isi', susun: 'Susun' }[item.station_isi] || item.station_isi
  const priorityColor = item.priority === 'urgent' ? 'bg-red-500 text-white'
    : item.priority === 'high' ? 'bg-orange-500 text-white'
    : 'bg-blue-500 text-white'

  return (
    <div className="bg-white rounded-xl shadow-sm border-l-4 border-l-emerald-500">
      <div className="p-4 border-b">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <h3 className="font-bold truncate">{item.judul}</h3>
            <p className="text-xs text-gray-500 font-mono">{item.spk_id} • {item.kode}</p>
          </div>
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${priorityColor}`}>
            {item.priority?.toUpperCase()}
          </span>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-gray-600 items-center">
          <Badge variant="default">{item.jenjang}</Badge>
          <span>📦 <strong>{Number(item.target_qty ?? item.print_qty ?? 0).toLocaleString()}</strong> eks</span>
          {item.target_qty != null && item.print_qty != null && item.target_qty !== item.print_qty && (
            <span className="text-amber-600 line-through">{Number(item.print_qty).toLocaleString()}</span>
          )}
          <span>• Stasiun: {stationLabel}</span>
          {item.deadline && <span>• DL: {new Date(item.deadline).toLocaleDateString('id-ID')}</span>}
        </div>
        {item.last_qc && (
          <div className="mt-2 text-xs bg-green-50 border border-green-200 rounded p-2 text-green-700">
            <CheckCircle className="w-3 h-3 inline mr-1" />
            Sudah pernah diinput hasil: {Number(item.last_qc.jumlah_akhir || 0).toLocaleString()} eks (reject: {Number(item.last_qc.jumlah_reject || 0).toLocaleString()})
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">📄 File ISI:</p>
          {item.files?.length > 0 ? (
            <div className="space-y-1">
              {item.files.map(f => (
                <div key={f.id} className="flex items-center justify-between gap-2 bg-gray-50 p-2 rounded">
                  <div className="min-w-0 flex items-center gap-2 flex-1">
                    <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                    <span className="text-xs truncate">{f.filename}</span>
                    <Badge variant={f.status === 'validated' ? 'success' : 'warning'}>
                      {f.status === 'validated' ? 'Tervalidasi' : f.status}
                    </Badge>
                  </div>
                  <button
                    onClick={() => onDownload(f)}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 shrink-0"
                  >
                    <Download className="w-3 h-3" /> Download
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">Belum ada file</p>
          )}
        </div>

        <button
          onClick={onOpen}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
        >
          <Printer className="w-4 h-4" /> Input Hasil Cetak & Kirim ke Binding
        </button>
      </div>
    </div>
  )
}

function CompleteModal({ item, onClose, onSuccess }) {
  const targetQty = item.target_qty ?? item.print_qty ?? 0
  const [jumlahAwal, setJumlahAwal] = useState(targetQty || '')
  const [jumlahReject, setJumlahReject] = useState('0')
  const [operatorName, setOperatorName] = useState('')
  const [catatan, setCatatan] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  const jumlahAkhir = Math.max(0, (parseInt(jumlahAwal) || 0) - (parseInt(jumlahReject) || 0))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErr(null)
    if (!jumlahAwal || parseInt(jumlahAwal) <= 0) return setErr('Jumlah cetak harus > 0')
    if (!operatorName.trim()) return setErr('Nama operator wajib diisi')
    try {
      setSubmitting(true)
      const res = await spkService.completeComcolor(item.spk_id, {
        jumlahAwal: parseInt(jumlahAwal),
        jumlahReject: parseInt(jumlahReject) || 0,
        jumlahAkhir,
        catatan: catatan.trim() || null,
        operatorName: operatorName.trim(),
      })
      if (res.success) onSuccess()
    } catch (e) {
      setErr(e.response?.data?.message || 'Gagal menyimpan')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-auto">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-600" /> Input Hasil Cetak Comcolor
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="bg-gray-50 p-3 rounded text-sm">
            <p className="font-medium">{item.judul}</p>
            <p className="text-xs text-gray-500 font-mono">{item.spk_id}</p>
            <p className="text-xs text-gray-600 mt-1">
              Target cetak: <strong>{Number(targetQty).toLocaleString()}</strong> eks
              {item.print_qty != null && item.print_qty !== targetQty && (
                <span className="ml-1 text-amber-600">(sebelumnya {Number(item.print_qty).toLocaleString()})</span>
              )}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Cetak *</label>
            <input
              type="number" min="1" required
              value={jumlahAwal}
              onChange={(e) => setJumlahAwal(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Reject</label>
            <input
              type="number" min="0"
              value={jumlahReject}
              onChange={(e) => setJumlahReject(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          <div className="bg-emerald-50 border border-emerald-200 p-3 rounded">
            <p className="text-sm text-emerald-700">
              Hasil Akhir: <span className="font-bold">{jumlahAkhir.toLocaleString()}</span> eks
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nama Operator *</label>
            <input
              type="text" required
              value={operatorName}
              onChange={(e) => setOperatorName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Nama operator comcolor"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
            <textarea
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              rows="2"
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Opsional"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 p-2 rounded text-xs text-amber-700 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Setelah disimpan, SPK akan langsung lanjut ke tahap <strong>Binding</strong> (lewati Potong Isi & Susun).</span>
          </div>

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">{err}</div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button" onClick={onClose}
              className="flex-1 py-2 border rounded-lg text-sm hover:bg-gray-50"
            >Batal</button>
            <button
              type="submit" disabled={submitting}
              className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {submitting ? <Clock className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Simpan & Kirim ke Binding
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ComcolorPage
